import { Card, List } from 'antd';
import classNames from 'classnames';
import sortBy from 'lodash/sortBy';
import React, { FC, useCallback, useMemo } from 'react';
import { Approval as ApprovalType, CreateApprovalPayload } from 'api/approval';
import { DateSeparator } from 'components/atoms/date-separator';
import { NewApprovalCard } from 'components/organisms/approvals/approval/new-approval-card';
import { Approval } from './approval';
import styles from './approvals.module.css';

interface ApprovalsProps {
  newData?: Partial<ApprovalType>;
  data: Array<ApprovalType>;
  onNewApprovalCancel: () => void;
  onNewApprovalSubmit: (arg: CreateApprovalPayload) => void;
  onApprovalItemDelete: (approvalId: number) => Promise<unknown>;
  onApprovalItemActionSubmit: (payload: any, options: any) => void;
  isApprovalItemActionLoading: boolean;
  isNewApprovalActionLoading: boolean;
  projectId: number;
  isPrivate: boolean;
  isFromDocument: boolean;
}

export const Approvals: FC<ApprovalsProps> = (props) => {
  const {
    data: approvals = [],
    newData,
    onNewApprovalCancel,
    onNewApprovalSubmit,
    isNewApprovalActionLoading,
    projectId,
    isPrivate,
    onApprovalItemDelete,
    onApprovalItemActionSubmit,
    isApprovalItemActionLoading,
    isFromDocument,
  } = props;

  const renderNewCardItem = useCallback(
    (approval) => (
      <Card key={approval.approvalId} className={styles.card}>
        <NewApprovalCard
          isPrivate={isPrivate}
          isLoading={isNewApprovalActionLoading}
          projectId={projectId}
          onCancel={onNewApprovalCancel}
          onSubmit={onNewApprovalSubmit}
          isFromDocument={isFromDocument}
        />
      </Card>
    ),
    [
      isFromDocument,
      isNewApprovalActionLoading,
      isPrivate,
      onNewApprovalCancel,
      onNewApprovalSubmit,
      projectId,
    ],
  );

  const approvalListRenderItem = useCallback(
    (approval: ApprovalType, index) => (
      <>
        <DateSeparator
          currentCreatedAt={approval.createdAt}
          previousCreatedAt={approvals[index - 1]?.createdAt}
        />

        <Card key={approval.approvalId} className={styles.card}>
          <Approval
            projectId={projectId}
            data={approval}
            onDelete={onApprovalItemDelete}
            onSubmit={onApprovalItemActionSubmit}
            isLoading={isApprovalItemActionLoading}
          />
        </Card>
      </>
    ),
    [
      approvals,
      isApprovalItemActionLoading,
      onApprovalItemActionSubmit,
      onApprovalItemDelete,
      projectId,
    ],
  );

  const newDataSource = [newData];
  const newCardListClassNames = classNames(styles.list, styles.new);

  const approvalData = useMemo(
    () => sortBy(approvals, 'createdAt').reverse(),
    [approvals],
  );

  return (
    <div className={styles.wrapper}>
      {newData && (
        <List
          key={0}
          className={newCardListClassNames}
          dataSource={newDataSource}
          itemLayout="vertical"
          pagination={false}
          split={false}
          renderItem={renderNewCardItem}
        />
      )}
      {approvals.length > 0 ? (
        <List
          className={styles.list}
          key={1}
          dataSource={approvalData}
          itemLayout="vertical"
          pagination={false}
          split={false}
          renderItem={approvalListRenderItem}
        />
      ) : null}
    </div>
  );
};
